import React, { useState, useRef, useEffect } from 'react';
import { Download, Save, Loader2, List, PlusCircle, Search, Trash2, CheckCircle, ExternalLink, FileDown } from 'lucide-react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import QRCode from "react-qr-code";
import Notification from '../../Component/Notification';
import { useAuth } from '../../context/AuthContext';
import { useAdminTheme } from '../context/AdminThemeContext';

const SKILL_INDIA_LOGO = "/skill-india.png"; 
const NSDC_LOGO = "/nsdc.png"; 

const AdminCertificatePage: React.FC = () => {
  const { token } = useAuth();
  const { colors } = useAdminTheme();
  
  const [activeTab, setActiveTab] = useState<'generate' | 'list'>('generate');
  const [formData, setFormData] = useState({
    studentName: "", candidateId: "", courseName: "",
    startDate: "", endDate: "", centerName: "Project Information Technology",
    certificateSerialId: "Fetching...", issueDate: new Date().toISOString().split('T')[0]
  });

  const [certList, setCertList] = useState<any[]>([]);
  const [loadingList, setLoadingList] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const certificateRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  
  const [notify, setNotify] = useState<{ isOpen: boolean; message: string; type: 'success' | 'error' }>({
    isOpen: false, message: '', type: 'success'
  });

  useEffect(() => { if(activeTab === 'generate') fetchId(); }, [activeTab, token]);
  useEffect(() => { if(activeTab === 'list') fetchCertificates(); }, [activeTab]);

  // ... (fetchId same as before)
  const fetchId = async () => {
      try {
        //   const res = await fetch('http://localhost:2025/api/certificates/next-id', { headers: { 'Authorization': `Bearer ${token}` } });
          const res = await fetch('https://www.projectinfotech.in/api/certificates/next-id', { headers: { 'Authorization': `Bearer ${token}` } });
          if (res.ok) {
              const serialId = await res.text();
              setFormData(prev => ({ ...prev, certificateSerialId: serialId }));
          }
      } catch (error) { setFormData(prev => ({ ...prev, certificateSerialId: "PIT/2026/0001" })); }
  };

  const fetchCertificates = async () => {
      setLoadingList(true);
      try {
        //   const res = await fetch('http://localhost:2025/api/certificates', { headers: { 'Authorization': `Bearer ${token}` } });
          const res = await fetch('https://www.projectinfotech.in/api/certificates', { headers: { 'Authorization': `Bearer ${token}` } });
          if(res.ok) setCertList(await res.json());
      } catch (err) { console.error(err); } 
      finally { setLoadingList(false); }
  };

  useEffect(() => {
    if (formData.studentName) {
        const year = new Date().getFullYear();
        const nameCode = formData.studentName.replace(/\s/g, '').substring(0, 3).toUpperCase();
        const rand = Math.floor(1000 + Math.random() * 9000);
        if (!formData.candidateId || formData.candidateId.length < 5) {
            setFormData(prev => ({ ...prev, candidateId: `CAN_${year}${nameCode}${rand}` }));
        }
    }
  }, [formData.studentName]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // --- NEW HANDLE SAVE (WITH PDF GENERATION) ---
  const handleSave = async () => {
    if(!formData.studentName || !formData.courseName) {
        setNotify({ isOpen: true, message: 'Name & Course required!', type: 'error' });
        return;
    }
    if (!certificateRef.current) return;

    setSaving(true);
    try {
        // 1. Generate PDF Blob silently
        const canvas = await html2canvas(certificateRef.current, { scale: 2, useCORS: true, backgroundColor: '#ffffff' });
        const imgData = canvas.toDataURL('image/png');
        const pdf = new jsPDF('l', 'mm', 'a4');
        pdf.addImage(imgData, 'PNG', 0, 0, 297, 210);
        const pdfBlob = pdf.output('blob');

        // 2. Prepare FormData
        const data = new FormData();
        data.append("file", pdfBlob, `${formData.studentName}_Certificate.pdf`);
        data.append("studentName", formData.studentName);
        data.append("candidateId", formData.candidateId);
        data.append("courseName", formData.courseName);
        data.append("certificateSerialId", formData.certificateSerialId);
        data.append("startDate", formData.startDate);
        data.append("endDate", formData.endDate);
        data.append("issueDate", formData.issueDate);
        data.append("centerName", formData.centerName);

        // 3. Send to Backend
        // const res = await fetch('http://localhost:2025/api/certificates/save', {
        const res = await fetch('https://www.projectinfotech.in/api/certificates/save', {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}` }, // Token needed
            body: data // No Content-Type header (browser sets it for FormData)
        });

        if (res.ok) {
            setNotify({ isOpen: true, message: 'Saved & PDF Uploaded!', type: 'success' });
            fetchId(); 
            setFormData(prev => ({ ...prev, studentName: "", candidateId: "", courseName: "" }));
        } else {
            setNotify({ isOpen: true, message: 'Save Failed', type: 'error' });
        }
    } catch (err) { 
        setNotify({ isOpen: true, message: 'Server Error during Save', type: 'error' }); 
        console.error(err);
    }
    finally { setSaving(false); }
  };

  const handleDelete = async (id: number) => {
      if(!token) {
          setNotify({ isOpen: true, message: 'Auth Error: No Token', type: 'error' });
          return;
      }
      if(!window.confirm("Are you sure?")) return;
      try {
        //   const res = await fetch(`http://localhost:2025/api/certificates/${id}`, {
          const res = await fetch(`https://www.projectinfotech.in/api/certificates/${id}`, {
              method: 'DELETE',
              headers: { 'Authorization': `Bearer ${token}` }
          });
          if(res.ok) {
              setNotify({ isOpen: true, message: 'Deleted Successfully', type: 'success' });
              setCertList(certList.filter(c => c.id !== id));
          } else {
              setNotify({ isOpen: true, message: 'Delete Failed', type: 'error' });
          }
      } catch (err) { setNotify({ isOpen: true, message: 'Server Error', type: 'error' }); }
  };

  // Direct Download from Browser (Preview)
  const handleDownloadPDF = async () => {
    if (!certificateRef.current) return;
    setLoading(true);
    try {
      const canvas = await html2canvas(certificateRef.current, { scale: 2, useCORS: true, backgroundColor: '#ffffff' });
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('l', 'mm', 'a4');
      pdf.addImage(imgData, 'PNG', 0, 0, 297, 210);
      pdf.save(`${formData.studentName}_Certificate.pdf`);
    } catch (err) { console.error(err); } 
    finally { setLoading(false); }
  };

  // Download from Server (List View)
  const handleServerDownload = async (id: number, fileName: string) => {
      try {
        //   const res = await fetch(`http://localhost:2025/api/certificates/download/${id}`, {
          const res = await fetch(`https://www.projectinfotech.in/api/certificates/download/${id}`, {
             headers: { 'Authorization': `Bearer ${token}` } 
          });
          if(res.ok) {
              const blob = await res.blob();
              const url = window.URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = `${fileName}.pdf`;
              document.body.appendChild(a);
              a.click();
              window.URL.revokeObjectURL(url);
          } else {
              setNotify({ isOpen: true, message: 'File not found on server', type: 'error' });
          }
      } catch(e) { console.error(e); }
  };

  const formatDate = (d: string) => {
      if(!d) return "";
      return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const PitStamp = () => (
    <div className="relative w-28 h-28 border-4 border-red-900 rounded-full flex items-center justify-center p-1 opacity-90 -rotate-12 mix-blend-multiply shadow-inner" style={{borderColor: '#7f1d1d'}}>
        <div className="w-full h-full border border-red-900 rounded-full flex flex-col items-center justify-center text-red-900 font-bold text-center leading-tight">
            <span className="text-[9px] tracking-widest uppercase font-serif">Authorized</span>
            <span className="text-3xl font-black tracking-tighter my-0.5">PIT</span>
            <span className="text-[10px] uppercase font-serif">Institute</span>
            <span className="text-[9px] tracking-widest mt-1">NOIDA • UP</span>
        </div>
    </div>
  );

  const filteredList = certList.filter(c => 
      (c.studentName || c.student_name || "").toLowerCase().includes(searchTerm.toLowerCase()) || 
      c.certificateSerialId.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-gray-50/50 p-4 md:p-8 font-sans">
      <Notification isOpen={notify.isOpen} message={notify.message} type={notify.type} onClose={() => setNotify({ ...notify, isOpen: false })} />

      {/* HEADER */}
      <div className="flex flex-col md:flex-row justify-between items-end mb-8 border-b border-gray-200 pb-4">
        <div>
            <h2 className={`text-2xl font-bold ${colors.navbarText}`}>Certificate Manager</h2>
            <p className="text-sm opacity-60 mt-1">Generate and manage student certificates</p>
        </div>
        <div className="flex space-x-2 mt-4 md:mt-0">
            <button onClick={() => setActiveTab('generate')} className={`flex items-center gap-2 px-5 py-2 rounded-lg font-medium transition-all ${activeTab === 'generate' ? colors.button3D + ' text-white shadow-lg' : 'bg-white border text-gray-600 hover:bg-gray-100'}`}>
                <PlusCircle size={18} /> Generate New
            </button>
            <button onClick={() => setActiveTab('list')} className={`flex items-center gap-2 px-5 py-2 rounded-lg font-medium transition-all ${activeTab === 'list' ? colors.button3D + ' text-white shadow-lg' : 'bg-white border text-gray-600 hover:bg-gray-100'}`}>
                <List size={18} /> Issued List
            </button>
        </div>
      </div>

      {/* TAB 1: GENERATE */}
      {activeTab === 'generate' && (
        <div className="animate-fade-in-up">
            <div className={`max-w-7xl mx-auto p-6 rounded-xl shadow-sm border mb-10 print:hidden ${colors.cardBg} ${colors.borderColor}`}>
                <div className="flex justify-between items-center mb-6 pb-4 border-b border-gray-100">
                    <h2 className={`text-lg font-bold ${colors.cardText}`}>Enter Details</h2>
                    <div className="flex gap-3">
                        <button onClick={handleSave} disabled={saving} className="bg-red-700 text-white px-4 py-2 rounded hover:bg-red-800 flex items-center gap-2 shadow-sm transition-transform active:scale-95">
                            {saving ? <Loader2 className="animate-spin w-4"/> : <Save className="w-4"/>} Save & Upload
                        </button>
                        <button onClick={handleDownloadPDF} disabled={loading} className="bg-gray-800 text-white px-4 py-2 rounded hover:bg-gray-900 flex items-center gap-2 shadow-sm transition-transform active:scale-95">
                            {loading ? <Loader2 className="animate-spin w-4"/> : <Download className="w-4"/>} Preview PDF
                        </button>
                    </div>
                </div>
                
                {/* FORM INPUTS */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-5 text-sm">
                    <div className="md:col-span-1"><label className="block text-xs font-bold opacity-70 mb-1">Serial ID</label><input type="text" value={formData.certificateSerialId} readOnly className="w-full p-2 border rounded bg-red-50 font-mono font-bold text-red-800 border-red-200" /></div>
                    <div className="md:col-span-1"><label className="block text-xs font-bold opacity-70 mb-1">Candidate ID</label><input type="text" name="candidateId" value={formData.candidateId} onChange={handleChange} className={`w-full p-2 border rounded ${colors.inputBg}`} /></div>
                    <div className="md:col-span-2"><label className="block text-xs font-bold opacity-70 mb-1">Student Name</label><input type="text" name="studentName" value={formData.studentName} onChange={handleChange} className={`w-full p-2 border rounded ${colors.inputBg}`} /></div>
                    <div className="md:col-span-2"><label className="block text-xs font-bold opacity-70 mb-1">Course Name</label><input type="text" name="courseName" value={formData.courseName} onChange={handleChange} className={`w-full p-2 border rounded ${colors.inputBg}`} /></div>
                    <div><label className="block text-xs font-bold opacity-70 mb-1">Start Date</label><input type="date" name="startDate" value={formData.startDate} onChange={handleChange} className={`w-full p-2 border rounded ${colors.inputBg}`} /></div>
                    <div><label className="block text-xs font-bold opacity-70 mb-1">End Date</label><input type="date" name="endDate" value={formData.endDate} onChange={handleChange} className={`w-full p-2 border rounded ${colors.inputBg}`} /></div>
                    <div><label className="block text-xs font-bold opacity-70 mb-1">Issue Date</label><input type="date" name="issueDate" value={formData.issueDate} onChange={handleChange} className={`w-full p-2 border rounded ${colors.inputBg}`} /></div>
                    <div className="md:col-span-3"><label className="block text-xs font-bold opacity-70 mb-1">Center Name</label><input type="text" name="centerName" value={formData.centerName} onChange={handleChange} className={`w-full p-2 border rounded ${colors.inputBg}`} /></div>
                </div>
            </div>

            {/* PREVIEW */}
            <div className="flex justify-center overflow-auto pb-10">
                <div ref={certificateRef} className="relative bg-white text-gray-900 shadow-2xl flex-shrink-0" style={{ width: '1123px', height: '794px', fontFamily: "'Times New Roman', serif" }}>
                    <div className="absolute inset-0 border-[25px] z-0" style={{ borderColor: '#800000' }}></div>
                    <div className="absolute top-0 right-0 w-[600px] h-[280px] z-10 pointer-events-none">
                         <svg viewBox="0 0 600 280" className="w-full h-full">
                            <path d="M0 0 L600 0 L600 280 L120 0 Z" fill="#800000" />
                            <path d="M100 0 L600 250 L600 260 L90 0 Z" fill="#D4AF37" opacity="0.9" />
                        </svg>
                        <div className="absolute top-8 right-8 text-right text-white font-sans">
                            <h2 className="text-3xl font-bold leading-tight tracking-wide">Project Information<br/>Technology</h2>
                            <p className="text-sm opacity-90 mt-1 text-yellow-100">Franchise of [OPS: Tech Solutions]</p>
                        </div>
                    </div>
                    <div className="absolute top-10 left-10 z-20 flex items-center gap-6 px-4 pt-2">
                        <img src={SKILL_INDIA_LOGO} alt="Skill India" className="h-20 object-contain" />
                        <div className="h-16 w-[1.5px] bg-gray-400"></div>
                        <img src={NSDC_LOGO} alt="NSDC" className="h-16 object-contain" />
                    </div>
                    <div className="absolute top-40 w-full text-center z-20">
                        <h1 className="text-6xl font-bold uppercase tracking-wide" style={{ color: '#800000', letterSpacing: '3px', textShadow: '1px 1px 0px rgba(0,0,0,0.1)' }}>CERTIFICATE OF TRAINING</h1>
                    </div>
                    <div className="absolute top-60 w-full text-center z-20 px-20">
                        <p className="text-2xl italic text-gray-500 mb-4 font-serif">This is to certify that</p>
                        <div className="inline-block relative"><h2 className="text-5xl font-bold uppercase tracking-wide px-10 pb-2 border-b-2 border-gray-400" style={{ color: '#2d2d2d' }}>{formData.studentName || "STUDENT NAME"}</h2></div>
                        <div className="flex justify-center items-center gap-3 text-xl mt-5 text-gray-600">
                            <span className="italic">having</span>
                            <div className="border border-gray-400 px-3 py-1 bg-white font-bold text-black shadow-sm font-sans text-lg">{formData.candidateId || "CAN_XXXXXX"}</div>
                            <span className="italic">has successfully completed training in</span>
                        </div>
                        <div className="mt-4 mb-4"><h3 className="text-4xl font-bold uppercase tracking-wider" style={{transform: 'scaleY(1.1)', color: '#800000'}}>{formData.courseName || "COURSE NAME"}</h3></div>
                        <div className="text-xl italic text-gray-600 space-y-2 mb-8">
                            <p>from the period <span className="font-bold text-gray-800 not-italic mx-2">{formatDate(formData.startDate)}</span> to <span className="font-bold text-gray-800 not-italic mx-2">{formatDate(formData.endDate)}</span></p>
                            <p>at <span className="font-bold text-gray-800 uppercase not-italic">{formData.centerName}</span></p>
                        </div>
                    </div>
                    <div className="absolute bottom-12 left-0 w-full px-16 flex justify-between items-end z-30">
                        <div className="text-left font-sans text-sm text-gray-700 pb-2">
                            <p className="font-bold text-lg">Date of Issue: <span className="font-normal ml-2 border-b border-gray-400 px-2">{formatDate(formData.issueDate)}</span></p>
                        </div>
                        <div className="flex flex-col items-center absolute left-1/2 transform -translate-x-1/2 bottom-2 bg-white px-4 py-2 rounded-t-xl z-40">
                            <p className="text-[10px] font-bold text-gray-500 mb-1 uppercase tracking-widest">Certificate ID</p>
                            <div className="border-2 px-4 py-1 font-bold text-xl mb-2 bg-white shadow-sm font-mono tracking-widest" style={{borderColor: '#800000', color: '#2d2d2d'}}>{formData.certificateSerialId}</div>
                            <div className="bg-white p-1.5 border border-gray-300">
                                <QRCode value={`http://localhost:3000/verify-certificate/${formData.certificateSerialId}`} size={80} />
                            </div>
                            <p className="text-[9px] mt-1 text-gray-400 uppercase">Scan to Verify</p>
                        </div>
                        <div className="text-center relative">
                            <div className="h-28 w-40 mb-1 mx-auto flex items-end justify-center relative"><div className="absolute bottom-2 right-4"><PitStamp /></div></div>
                            <div className="border-t border-gray-500 w-56 mx-auto pt-2">
                                <p className="font-bold text-sm text-gray-800">Signing Authority</p>
                                <p className="text-xs font-bold mt-0.5 tracking-wide" style={{color: '#800000'}}>PIT INSTITUTE</p>
                                <p className="text-[9px] text-gray-500 mt-0.5 uppercase">An ISO 9001:2015 Certified</p>
                            </div>
                        </div>
                    </div>
                    <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-0 opacity-[0.03] pointer-events-none">
                        <h1 className="text-[180px] font-black text-gray-900 rotate-[-25deg] tracking-widest border-[10px] border-gray-900 p-10 rounded-3xl">PIT</h1>
                    </div>
                </div>
            </div>
        </div>
      )}

      {/* TAB 2: LIST */}
      {activeTab === 'list' && (
        <div className="animate-fade-in-up">
            <div className={`p-4 rounded-xl border flex items-center gap-3 mb-6 shadow-sm ${colors.cardBg} ${colors.borderColor}`}>
                <Search className="text-gray-400" size={20} />
                <input type="text" placeholder="Search by Student Name or Certificate ID..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className={`flex-1 bg-transparent outline-none ${colors.cardText}`} />
            </div>
            <div className={`rounded-xl shadow-sm border overflow-hidden ${colors.cardBg} ${colors.borderColor}`}>
                {loadingList ? (
                    <div className="p-10 flex justify-center"><Loader2 className="animate-spin text-red-600" size={30} /></div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse">
                            <thead className={`${colors.tableHeaderBg}`}>
                                <tr>
                                    <th className="p-4 text-sm font-semibold">Serial ID</th>
                                    <th className="p-4 text-sm font-semibold">Student Name</th>
                                    <th className="p-4 text-sm font-semibold">Course</th>
                                    <th className="p-4 text-sm font-semibold">Issue Date</th>
                                    <th className="p-4 text-sm font-semibold text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody className={`${colors.cardText}`}>
                                {filteredList.length > 0 ? filteredList.map((cert) => (
                                    <tr key={cert.id} className={`border-t ${colors.borderColor} hover:opacity-80 transition-colors`}>
                                        <td className="p-4 font-mono text-xs font-bold text-red-600">{cert.certificateSerialId}</td>
                                        <td className="p-4"><div className="font-bold text-sm">{cert.studentName}</div><div className="text-xs opacity-50">{cert.candidateId}</div></td>
                                        <td className="p-4"><span className="bg-blue-100 text-blue-700 px-2 py-1 rounded text-xs font-bold border border-blue-200">{cert.courseName}</span></td>
                                        <td className="p-4 text-sm opacity-80">{formatDate(cert.issueDate)}</td>
                                        <td className="p-4 flex justify-end gap-2">
                                            
                                            {/* DOWNLOAD PDF FROM SERVER */}
                                            {cert.pdfPath && (
                                                <button 
                                                    onClick={() => handleServerDownload(cert.id, cert.studentName)} 
                                                    className="p-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition" 
                                                    title="Download Stored PDF"
                                                >
                                                    <FileDown size={16} />
                                                </button>
                                            )}

                                            <a href={`http://localhost:3000/verify-certificate/${cert.certificateSerialId}`} target="_blank" rel="noreferrer" className="p-2 bg-green-50 text-green-600 rounded-lg hover:bg-green-100 transition" title="Verify Link"><ExternalLink size={16} /></a>
                                            <button onClick={() => handleDelete(cert.id)} className="p-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition" title="Delete Record"><Trash2 size={16} /></button>
                                        </td>
                                    </tr>
                                )) : (
                                    <tr><td colSpan={5} className="p-10 text-center opacity-50">No certificates found.</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
      )}
    </div>
  );
};

export default AdminCertificatePage;